/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const HowToGetCitedByChatgptBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      ChatGPT now answers 1+ billion queries a week — and on most of them it cites 2–5
      sources. Being one of those sources is the new "page one of Google." It drives traffic,
      builds authority, and compounds your brand in ways classic SEO can't. Here's exactly
      how to get cited.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="what-citation-means">What does "cited by ChatGPT" actually mean?</h2>
    <p>
      When a user asks ChatGPT a question with the Search tool on, the answer includes inline
      footnotes linking to web pages. Each footnote is a <strong>citation</strong>. The cited
      site gets a referral link, a name-drop inside the answer, and often a follow-up question
      reply chain that keeps the user on-topic.
    </p>
    <p>
      Citations are not the same as backlinks. ChatGPT doesn't <em>link to</em> your domain in
      the way Google does — it <em>quotes from</em> your page and credits you. That's the new
      currency.
    </p>

    <h2 id="how-chatgpt-picks">The 5 things ChatGPT looks at when picking sources</h2>
    <p>
      From reverse-engineering hundreds of cited pages, five signals dominate which sources
      ChatGPT chooses for an answer:
    </p>
    <div className="my-6 overflow-hidden rounded-2xl border border-white/10">
      <table className="w-full text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Signal</th>
            <th className="p-3">Why it matters</th>
            <th className="p-3">Weight</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["Bing index rank", "ChatGPT Search runs on Bing's index. If you're not on Bing page 1–2, you're invisible.", "★★★★★"],
            ["FAQ / HowTo / Article schema", "Tells ChatGPT exactly what kind of content this is. Schema-tagged answers get pulled verbatim.", "★★★★★"],
            ["Lead-with-the-answer structure", "First 2 sentences after a question-style H2 are what gets quoted.", "★★★★★"],
            ["Brand entity recognition", "Organization + Person JSON-LD + sameAs links. ChatGPT cites sources it can disambiguate.", "★★★★"],
            ["Unlinked brand mentions", "Reddit threads, Substack newsletters, podcast transcripts naming your brand. Weighted heavily.", "★★★★"],
            ["Freshness", "Recent dates and refreshed content rank dramatically higher in AI answers than older originals.", "★★★"],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-white" : i === 2 ? "text-[#F09819]" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Callout kind="info">
      <strong>Counterintuitive truth:</strong> Bing rank matters far more than Google rank for
      ChatGPT citations. Submit your sitemap to Bing Webmaster Tools today — most brands
      forget.
    </Callout>

    <h2 id="step-by-step">Step-by-step: get cited by ChatGPT in 6 weeks</h2>

    <h3>Week 1 — Audit and unblock</h3>
    <ol>
      <li>Open <code>/robots.txt</code>. Allow <code>GPTBot</code>, <code>ChatGPT-User</code>, <code>OAI-SearchBot</code>, and <code>CCBot</code> explicitly.</li>
      <li>Submit your sitemap to <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer">Bing Webmaster Tools</a>.</li>
      <li>Verify your top 10 pages are indexed by Bing (not just Google). If not, request indexing manually.</li>
    </ol>

    <h3>Week 2 — Restructure your top 5 pages</h3>
    <ol>
      <li>Rewrite every H2 as a question your buyer would ask out loud.</li>
      <li>Add a 50-word answer block directly under each question.</li>
      <li>Convert dense paragraphs into bullet lists and tables.</li>
      <li>Confirm each answer block is self-contained (no "as we'll see below" hedging).</li>
    </ol>

    <h3>Week 3 — Inject schemas</h3>
    <ol>
      <li>Add <code>FAQPage</code> JSON-LD for every Q&amp;A block.</li>
      <li>Add <code>HowTo</code> JSON-LD for any step-by-step content.</li>
      <li>Add <code>Article</code> or <code>BlogPosting</code> JSON-LD on every post with author/datePublished/dateModified.</li>
      <li>Validate with{" "}
        <a href="https://search.google.com/test/rich-results" target="_blank" rel="noopener noreferrer">
          Google's Rich Results Test
        </a>{" "}
        — if it parses, ChatGPT can too.
      </li>
    </ol>

    <h3>Week 4 — Build your entity graph</h3>
    <ol>
      <li>Publish <code>Organization</code> JSON-LD on your site root with logo + founder + sameAs.</li>
      <li>Publish <code>Person</code> JSON-LD for every author with credentials and links to LinkedIn / Wikipedia / Crunchbase.</li>
      <li>Add a public "About" page and link it from the footer.</li>
      <li>Drop <code>/llms.txt</code> at your domain root listing your most quotable pages.</li>
    </ol>

    <h3>Week 5 — Earn unlinked mentions</h3>
    <ol>
      <li>Post one high-value answer in 3 relevant subreddits where your category lives.</li>
      <li>Pitch one podcast in your niche — get named in the show description.</li>
      <li>Write one guest essay for a Substack newsletter in your space.</li>
      <li>Submit your tool to 5 niche directories (e.g. Product Hunt, AlternativeTo, G2).</li>
    </ol>

    <h3>Week 6 — Track and iterate</h3>
    <ol>
      <li>Run 10 category prompts in ChatGPT Search. Log: are you named? Linked?</li>
      <li>Compare to baseline from week 1.</li>
      <li>Identify which 1–2 pages got cited. Double down on that structure.</li>
    </ol>

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want all 6 weeks done for you?",
        sub: "Soro restructures, schemas, and publishes ChatGPT-ready content daily. Set it up in 60 seconds.",
        cta: "Try Soro free",
      }}
    />

    <h2 id="how-to-check">How to check if you're already being cited</h2>
    <p>There's no Search Console for ChatGPT yet — but three free methods work:</p>
    <ul>
      <li><strong>Manual prompt audit.</strong> Open ChatGPT with Search on. Ask 10 buyer-intent questions in your category. Log every citation, weekly.</li>
      <li><strong>Server log spike check.</strong> Look for spikes in referrals from <code>chat.openai.com</code> or <code>chatgpt.com</code> in your analytics.</li>
      <li><strong>AI citation tracking tools.</strong> <a href="https://www.profound.ai" target="_blank" rel="noopener noreferrer">Profound</a>, Otterly, and Peec.ai automate this across ChatGPT + Perplexity + AI Overviews.</li>
    </ul>

    <h2 id="business-impact">What does a single ChatGPT citation actually do?</h2>
    <p>From our own data on five sites we run, a single sustained citation drives:</p>
    <ul>
      <li><strong>40–120 referral clicks per month</strong> per citation (small numbers but extreme intent)</li>
      <li><strong>3–5x higher conversion rate</strong> than organic Google clicks — users have already "interviewed" you via the AI</li>
      <li><strong>Branded search lift</strong> on Google within 60 days — citations create awareness that converts to direct queries</li>
      <li><strong>Defensive moat.</strong> Once ChatGPT has decided you're the authority, displacing you is hard</li>
    </ul>

    <h2 id="common-reasons">5 reasons ChatGPT skips your page</h2>
    <ol>
      <li><strong>You're blocked.</strong> Default robots.txt disallows <code>GPTBot</code> — audit it today.</li>
      <li><strong>You're not on Bing.</strong> Submit your sitemap to Bing Webmaster Tools.</li>
      <li><strong>Your answer is buried.</strong> ChatGPT can't extract from 400 words of intro.</li>
      <li><strong>No schema.</strong> Without FAQ/HowTo/Article JSON-LD, you're a wall of text.</li>
      <li><strong>No entity signals.</strong> Without Organization + Person schema, ChatGPT doesn't know who you are.</li>
    </ol>

    <FaqBlock faq={post.faq} />

    <h2 id="next">What to do next</h2>
    <p>
      If you're starting from zero on AEO/GEO, read{" "}
      <a href="/blog/how-to-rank-on-ai-search-engines">how to rank on AI search engines</a>{" "}
      first — it's the foundation for everything in this guide. Then come back here for the
      ChatGPT-specific tactics.
    </p>
    <p>
      Want all of this automated? <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>Soro</a> ships ChatGPT-ready articles daily — structured, schemas-injected, entity-tagged, and submitted to Bing automatically.
    </p>
  </>
);
